'use client';

/**
 * usePurchaseFlowCore Hook
 *
 * Encapsulates all state, hook calls, effects, derived values, and handlers
 * for PurchaseFlowContent. The component becomes a thin orchestrator that
 * calls this hook and delegates rendering to display-mode components.
 */

import React, {
  useState, useEffect, useMemo, useCallback, useRef
} from 'react';
import type {
  PurchaseFlowProps, PurchaseType, PaymentMethodKind, PaymentMethod, PaymentOption,
  PurchaseDetails, Screen, UseChargeProcessingReturn,
  FetchDataParams, SupportedCurrency
} from '../types';
import type { UsePurchaseFlowHandlersResult } from './usePurchaseFlowHandlers';
import type { ProcessorCallbacks, ProcessingScreenCallbacks } from './useProcessorCallbacks';
import { usePaymentData } from './usePaymentData';
import { useNavigation } from './useNavigation';
import { useNativeBackButton } from './useNativeBackButton';
import { usePurchaseFlowHandlers } from './usePurchaseFlowHandlers';
import { useChargeProcessing } from './useChargeProcessing';
import { useProcessorCallbacks } from './useProcessorCallbacks';
import { usePaygValidation } from './usePaygValidation';
import { usePaymentSupport } from './usePaymentSupport';
import { useResetOnChange } from './useResetOnChange';
import {
  SCREENS, getScreenTitleKey, PURCHASE_FLOW_ICONS, PAYMENT_ERROR_TYPES, PURCHASE_FLOW_TEXT_KEYS,
  PAYMENT_SOURCE_ERRORS
} from '../utils/constants';
import { formatAmount } from '../utils/formatAmount';
import { getStatementNote } from '../utils/statementNote';
import { usePurchaseAnalytics } from '../analytics';
import {
  usePurchaseRender, usePurchaseCaptcha,
  useMinPurchaseAmount, useClientAvailableCredit, usePurchaseLog
} from '../config/PurchaseFlowConfigProvider';
import { getKindConfig } from '../config/gatewayRegistry';
import type { PerformSetupOptions } from '../config/gatewayRegistry';
import { createPaymentPopup } from '../utils/paymentPopup';
import { isWebProcessedPayment } from '../gateways/componentSelector';
import { addPaymentSource, getPaymentSourceConfig, fetchPaymentSources } from '../api/paymentSources';
import {
  mapPaymentMethodFromApi,
  mapPaymentOptionsFromApi,
  ensurePaymentOptionsWithIap,
  getIapDefaultIfNoMethods
} from '../utils/dataTransformers';
import { showToast } from '../components/Toast';
import classes from '../screens/classes.module.scss';

// ============================================================================
// Header Configuration (pure function, no hooks)
// ============================================================================

export interface HeaderConfig {
  titleKey: string;
  showBackButton: boolean;
  showCloseButton: boolean;
}

type FlowMode = PurchaseFlowProps['mode'];

export const getHeaderConfig = (
  currentScreen: Screen,
  isPayg: boolean,
  processingComplete: boolean,
  processingErrorType: string | undefined,
  mode: FlowMode = 'purchase',
  isMinPurchase = false,
  allowCloseButton = true
): HeaderConfig => {
  const isProcessingScreen = currentScreen === SCREENS.PAYMENT_PROCESSING;
  const titleKey = isProcessingScreen ? '' : getScreenTitleKey(currentScreen, isPayg, mode, isMinPurchase);

  const isInnerScreen = currentScreen === SCREENS.PAYMENT_METHODS_LIST
    || currentScreen === SCREENS.ADD_CREDIT_CARD;
  const isAdditionalFundsEntry = currentScreen === SCREENS.PURCHASE_DETAILS
    && mode === 'additionalFunds';
  const showBackButton = isInnerScreen || isAdditionalFundsEntry;

  const isProcessingSuccess = currentScreen === SCREENS.PAYMENT_PROCESSING
    && processingComplete && !processingErrorType;
  const isSpendingLimitsError = currentScreen === SCREENS.PAYMENT_PROCESSING
    && processingErrorType === PAYMENT_ERROR_TYPES.SPENDING_LIMITS;
  const showCloseButton = allowCloseButton
    && ((currentScreen === SCREENS.PURCHASE_DETAILS && !isAdditionalFundsEntry)
      || isProcessingSuccess || isSpendingLimitsError);

  return { titleKey, showBackButton, showCloseButton };
};

// ============================================================================
// Hook Return Type
// ============================================================================

export interface UsePurchaseFlowCoreReturn {
  // Data
  loading: boolean;
  error: string | null;
  dataLoaded: boolean;
  selectedPaymentMethod: PaymentMethod | null;
  paymentMethods: PaymentMethod[];
  paymentOptions: PaymentOption[];
  purchaseDetails: PurchaseDetails | null;
  /** Payment amount sent to /combined for fee calculation (min purchase applied). */
  price: number;
  /** Bonus amount (from props). */
  bonus: number;
  /** Fee-adjusted payment amount (totalAmount from fees, or fallback to price). Used for native button sessions. */
  feeAdjustedAmount: number;
  /** Fee description text from API (e.g. "Includes $0.50 processing fee"). */
  purchaseFeesText?: string;
  /**
   * USD credits acquired by this transaction. Sent to both
   * `/combined.credits_requested` (echoed back in local currency and shown at the
   * top via `purchaseDetails.fees.creditsRequested`) and `/process_credits/{amount}`
   * (the actual charge). In `additionalFunds` mode this is just the delta after
   * the existing balance offset.
   */
  creditsRequested: number;
  isMinPurchase: boolean;
  detailsSlot: React.ReactNode;
  /**
   * Server-confirmed currency (resolved from fees.currency via supportedCurrencies).
   * Starts as the initial lookup from currencyKey; updated after /combined responds.
   */
  currency: SupportedCurrency;
  /**
   * Server-confirmed exchange rate (fees.fx_rate from /combined).
   * Starts as the initial fxRate prop; updated after /combined responds.
   * Used for /process_credits calls.
   */
  fxRate: number;

  // Payment support
  supportCheckLoading: boolean;
  isSelectedMethodSupported: boolean;

  // Processing
  chargeProcessing: UseChargeProcessingReturn;
  processingComplete: boolean;
  processingHasError: boolean;
  buttonProcessing: boolean;
  setButtonProcessing: (v: boolean) => void;

  // Validation
  actionInProgress: boolean;
  validating: boolean;
  validationError: string | null;
  dismissValidationError: () => void;
  handleValidationErrorBack: () => void;

  // Navigation
  currentScreen: Screen;
  headerConfig: HeaderConfig;

  // Derived
  statementNote: string | undefined;
  /**
   * Overlay renderer for the Popover's renderTopOverlay prop.
   * Returns the error icon renderer, the affiliate promo logo renderer,
   * or undefined when no overlay is needed.
   */
  renderTopOverlay: (() => React.ReactElement) | undefined;

  // Handlers
  handlePayClick: () => void;
  handleBackClick: () => void;
  handleCloseClick: () => void;
  handleRetryClick: () => void;
  handleAddPaymentMethodClick: (kind: string) => void;
  handlePaymentMethodAdded: () => Promise<void>;
  handleEditPaymentMethod: (id: number, kind: string) => Promise<void>;
  handleLoadMethods: () => Promise<void>;
  onPerformSetup: (kind: string) => Promise<void>;

  // Handler bundles (for screens that need multiple handlers)
  handlers: UsePurchaseFlowHandlersResult;
  screenCallbacks: ProcessingScreenCallbacks;
  processorCallbacks: ProcessorCallbacks;

  // For ProcessorElement
  startSignal: number;
  retryKey: number;
  recoverFlag: boolean;
  recoverBaseUrl?: string;
  clickContextRef: React.RefObject<any>;

  // Native pay button prepared by the processor (e.g. Google Pay — MBW-2436)
  /** Shared trigger ref the processor fills with a synchronous "open sheet" fn. */
  nativeTriggerRef: React.RefObject<(() => void) | null>;
  /** True once the processor's native pay button is mounted and ready to tap. */
  nativeButtonReady: boolean;
  /** Called by the processor when its native pay button becomes ready. */
  handleNativeButtonReady: () => void;

  // For AddPaymentMethodScreen
  addingPaymentMethodKind: PaymentMethodKind | null;
  editingPaymentSourceId: number | undefined;

  // For PaymentProcessingScreen
  focusProcessingWindow: () => void;
}

// ============================================================================
// Hook Props (subset of PurchaseFlowProps consumed by the hook)
// ============================================================================

type UsePurchaseFlowCoreProps = Omit<PurchaseFlowProps, 'config'>;

// ============================================================================
// Hook Implementation
// ============================================================================

export const usePurchaseFlowCore = ({
  price: priceProp,
  creditsRequested: creditsRequestedProp,
  bonus = 0,
  isPayg = false,
  fxRate,
  currencyKey,
  supportedCurrencies,
  metadata,
  onReady,
  onComplete,
  onFailed,
  onCancel,
  showSuccessScreen = true,
  showErrorScreen,
  showCloseButton: allowCloseButton = true,
  onScreenChange,
  onPaymentMethodAdded,
  onPurchaseSuccess,
  onSpendingLimitAction,
  onDelegatedPaymentStarted,
  mode = 'purchase',
  displayMode,
  mobileAppProps,
  purchaseType
}: UsePurchaseFlowCoreProps): UsePurchaseFlowCoreReturn => {
  // ==========================================================================
  // State
  // ==========================================================================

  const [dataLoaded, setDataLoaded] = useState(false);
  const [addingPaymentMethodKind, setAddingPaymentMethodKind] = useState<PaymentMethodKind | null>(null);
  const [editingPaymentSourceId, setEditingPaymentSourceId] = useState<number | undefined>();
  const [processingHasError, setProcessingHasError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [recoverFlag, setRecoverFlag] = useState(false);
  const [recoverBaseUrl, setRecoverBaseUrl] = useState<string | undefined>();
  const [actionInProgress, setActionInProgress] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const clickContextRef = useRef<any>(null);
  const addFundsScreenTrackedRef = useRef(false);
  const paymentMethodsListScreenTrackedRef = useRef(false);
  const [buttonProcessing, setButtonProcessing] = useState(false);
  const [startSignal, setStartSignal] = useState(0);

  // Google Pay (Checkout.com Flow) prepares its payment up front and owns the
  // click→sheet (MBW-2436). The processor populates `nativeTriggerRef` with a
  // synchronous "open the sheet" trigger when ready and signals readiness via
  // `handleNativeButtonReady`; the Pay button is gated on `nativeButtonReady`,
  // and handlePayClick invokes the trigger directly in the gesture.
  const nativeTriggerRef = useRef<(() => void) | null>(null);
  const [nativeButtonReadyState, setNativeButtonReady] = useState(false);
  const handleNativeButtonReady = useCallback(() => setNativeButtonReady(true), []);
  // Quick top-up charges via ChargeViaAPI, not the GP Flow processor, so the GP
  // button never mounts and `onReady` never fires. Treat it as ready so the Pay
  // button isn't stuck on an infinite spinner (MBW-2436).
  const nativeButtonReady = metadata?.topupFlow === 'quick' || nativeButtonReadyState;

  // ==========================================================================
  // Hooks
  // ==========================================================================

  const analytics = usePurchaseAnalytics();
  const log = usePurchaseLog();
  const withCaptcha = usePurchaseCaptcha();
  const minPurchaseAmount = useMinPurchaseAmount();
  const clientAvailableCredit = useClientAvailableCredit();
  const { renderIcon, renderText } = usePurchaseRender();

  // ==========================================================================
  // Pricing (MBW-2251)
  // ==========================================================================
  //
  // USD values that flow into backend requests. Local-currency UI values come from
  // /combined's response (`purchaseDetails.fees.*`, `purchaseDetails.availableCredit`)
  // — see dataTransformers.ts.
  //
  //   price             — payment amount sent to /combined.amount_to_pay; the button
  //                       reads it back as fees.totalAmount (price + fees).
  //   creditsRequested  — credits acquired by this transaction. Sent to both
  //                       /combined.credits_requested (API echoes it as
  //                       fees.creditsRequested, which is what the top section shows)
  //                       and /process_credits/{amount} (the actual charge URL).
  //
  // Top vs button:
  //   - Sidebar / regular purchase:  top = credits received (incl. discount value),
  //                                  button = amount paid.
  //   - Discount "12 for $11.99":    top = $12, button = $11.99.
  //   - Additional funds (chat):     top = amount the user still needs to pay (the
  //                                  delta after balance offset), button = same.
  //                                  The header "additional funds required" already
  //                                  frames the number.
  //
  // The details slot (additionalFunds only) breaks down the session cost / balance /
  // natural difference and uses an asterisk when min purchase forces the user to
  // pay more than the natural difference (MBW-2398).
  //
  // | Scenario                              | price | creditsRequested |
  // |---------------------------------------|-------|------------------|
  // | Regular $10 purchase                  | 10    | 10               |
  // | Discount "15 for $14.99"              | 14.99 | 15               |
  // | Min purchase ($5) on $4 purchase      | 5     | 5                |
  // | Session $5, balance $3                | 2     | 2                |
  // | Session $4, balance $0, min $5        | 5     | 5                |
  // | Session $10, balance $8, min $5       | 5     | 5                |

  const SKIP_MIN_PURCHASE_TYPES: PurchaseType[] = ['affiliate_promo_popup', 'advisor_bonus', 'quick_top_up'];
  const skipMinPurchase = isPayg || SKIP_MIN_PURCHASE_TYPES.includes(purchaseType);
  const applyMinPurchase = (amount: number) => (
    skipMinPurchase || amount <= 0 ? amount : Math.max(amount, minPurchaseAmount)
  );

  const usesBalance = mode === 'additionalFunds';
  const rawPrice = Math.max(0, priceProp - (usesBalance ? clientAvailableCredit : 0));
  const price = applyMinPurchase(rawPrice);
  const isMinPurchase = price > rawPrice;

  const creditsRequested = (usesBalance || isMinPurchase) ? price : (creditsRequestedProp ?? price);

  // ==========================================================================
  // Data & Navigation Hooks
  // ==========================================================================

  const chargeProcessing = useChargeProcessing();
  const onPurchaseDetailsLoaded = useCallback((details: PurchaseDetails) => {
    analytics.updateConfig({
      purchaseFees: details.fees.purchaseFees,
      localAmount: details.fees.amountToPay
    });
  }, [analytics]);
  const onPaymentOptionsLoaded = useCallback((options: PaymentOption[]) => {
    analytics.updateConfig({ availableMethods: options.map((o) => o.kind) });
  }, [analytics]);
  const paymentData = usePaymentData({
    withCaptcha,
    purchaseType,
    onPurchaseDetailsLoaded,
    onPaymentOptionsLoaded,
    fxRate,
    currencyKey,
    supportedCurrencies
  });

  const {
    loading,
    error,
    paymentMethods,
    paymentOptions,
    purchaseDetails,
    addingExtraParams,
    selectedPaymentMethod,
    fetchData,
    fxRate: confirmedFxRate,
    currency
  } = paymentData;

  // Details slot (balance breakdown — only in additionalFunds mode).
  //
  // "Required funds" always reflects the session cost (`priceProp`) and "Additional funds"
  // the natural difference (`priceProp − balance`). The asterisk indicates that the user
  // is paying more than that natural difference because min purchase kicked in (MBW-2398).
  // Balance comes from the API; the other two are USD × fxRate since /combined doesn't
  // echo them in this shape.
  const detailsSlot = useMemo(() => {
    if (mode !== 'additionalFunds') return undefined;

    const sign = currency?.sign;
    const requiredFunds = priceProp * confirmedFxRate;
    const additionalFunds = Math.max(0, priceProp - clientAvailableCredit) * confirmedFxRate;
    const balance = purchaseDetails?.availableCredit ?? clientAvailableCredit * confirmedFxRate;

    const durationSeconds = metadata?.duration;
    const requiredFundsLabel = durationSeconds
      ? renderText(PURCHASE_FLOW_TEXT_KEYS.DETAILS_REQUIRED_FUNDS_WITH_MIN, { mins: durationSeconds / 60 })
      : renderText(PURCHASE_FLOW_TEXT_KEYS.DETAILS_REQUIRED_FUNDS);

    return (
      <>
        <div className={ classes.detailsDivider } />
        <div className={ classes.detailsRow }>
          {requiredFundsLabel}
          {' '}
          {formatAmount(requiredFunds, sign)}
        </div>
        <div className={ classes.detailsRow }>
          {renderText(PURCHASE_FLOW_TEXT_KEYS.DETAILS_CURRENT_BALANCE)}
          {' '}
          {formatAmount(balance, sign)}
        </div>
        <div className={ classes.detailsDivider } />
        <div className={ classes.detailsRow }>
          <b>
            {renderText(PURCHASE_FLOW_TEXT_KEYS.DETAILS_ADDITIONAL_FUNDS)}
            {' '}
            {formatAmount(additionalFunds, sign)}
          </b>
          {isMinPurchase && <span className={ classes.detailsInfoStar }>*</span>}
        </div>
      </>
    );
  }, [mode, purchaseDetails, isMinPurchase, priceProp, clientAvailableCredit, confirmedFxRate, metadata?.duration, renderText, currency]);

  useResetOnChange(selectedPaymentMethod?.id, () => {
    if (startSignal > 0) {
      setStartSignal(0);
    }
    // Re-arm the native-button gate — the newly selected processor must re-signal
    // readiness (and re-populate its trigger) before its Pay button is enabled.
    setNativeButtonReady(false);
    nativeTriggerRef.current = null;
  });

  const navigation = useNavigation();
  const { currentScreen, goBack } = navigation;

  const paymentSupportItems = useMemo(
    () => (selectedPaymentMethod ? [selectedPaymentMethod] : []),
    [selectedPaymentMethod?.kind]
  );
  const { supportMap: paymentSupportMap, loading: supportCheckLoading } = usePaymentSupport(paymentSupportItems);
  const isSelectedMethodSupported = paymentSupportMap[selectedPaymentMethod?.kind] === true;

  const fetchParams: FetchDataParams = useMemo(() => ({
    price,
    bonus,
    creditsRequested
  }), [price, bonus, creditsRequested]);

  const feeAdjustedAmount = purchaseDetails?.fees.totalAmount || price;

  // ==========================================================================
  // Validation Handlers
  // ==========================================================================

  const handleValidationSuccess = useCallback((result: { success: boolean; validationId?: number; okMessage?: string }) => {
    setValidating(false);
    setValidationError(null);
    onComplete?.(result);
  }, [onComplete]);

  const handleValidationError = useCallback((errorMsg: string) => {
    setValidating(false);
    setValidationError(errorMsg);
  }, []);

  const paygValidation = usePaygValidation({
    paymentSourceId: selectedPaymentMethod?.id || 0,
    advisorId: metadata?.advisorId,
    conversationType: metadata?.conversationType,
    context: metadata?.context,
    methodName: selectedPaymentMethod?.label || selectedPaymentMethod?.kind || 'Payment Method',
    onSuccess: handleValidationSuccess,
    onError: handleValidationError
  });

  const startInlineValidation = useCallback(() => {
    setValidating(true);
    setValidationError(null);
    paygValidation.reset();
    paygValidation.startValidation();
  }, [paygValidation]);

  const dismissValidationError = useCallback(() => {
    setValidationError(null);
    setActionInProgress(false);
  }, []);

  const handleValidationErrorBack = useCallback(() => {
    setValidationError(null);
    setActionInProgress(false);
    onCancel?.('user_closed');
  }, [onCancel]);

  // ==========================================================================
  // Flow Handlers
  // ==========================================================================

  const onDefaultChanged = useCallback(() => {
    showToast(renderText(PURCHASE_FLOW_TEXT_KEYS.PAYMENT_SOURCES_DEFAULT_UPDATED));
  }, [renderText]);

  const handlers = usePurchaseFlowHandlers({
    analytics,
    navigation,
    paymentData: {
      selectedPaymentMethod,
      paymentMethods,
      addingExtraParams,
      fetchData,
      setSelectedPaymentMethod: paymentData.setSelectedPaymentMethod,
      updatePaymentMethods: paymentData.updatePaymentMethods
    },
    options: {
      isPayg,
      fetchParams,
      onComplete,
      onFailed,
      onCancel,
      onStartValidation: startInlineValidation,
      onDefaultChanged,
      onDelegatedPaymentStarted
    }
  });

  const refreshDataForSetup = useCallback(
    () => fetchData(fetchParams.price, fetchParams.bonus, isPayg, fetchParams.creditsRequested),
    [fetchData, fetchParams, isPayg]
  );

  const handleLoadMethods = useCallback(async () => {
    try {
      const response = await fetchPaymentSources(isPayg);
      const methods = (response.existing || []).map(mapPaymentMethodFromApi);
      const selected = getIapDefaultIfNoMethods(methods, selectedPaymentMethod, purchaseType);
      const options = ensurePaymentOptionsWithIap(mapPaymentOptionsFromApi(response.available), purchaseType);

      paymentData.updatePaymentMethods(methods, selected);
      paymentData.setPaymentOptions(options);
    } catch {
      // Silently fail — show stale data rather than break the screen
    }
  }, [selectedPaymentMethod, paymentData, isPayg, purchaseType]);

  const onPerformSetup = useCallback(async (kind: string) => {
    const config = getKindConfig(kind);
    if (!config?.performSetup) return;

    const ctx = (selectedPaymentMethod?.kind === kind ? selectedPaymentMethod.context : undefined)
      || paymentOptions.find((opt) => opt.kind === kind)?.context;
    const sourceId = (selectedPaymentMethod?.kind === kind) ? selectedPaymentMethod.id : 0;

    const setupOpts: PerformSetupOptions = {
      context: ctx,
      paymentSourceId: sourceId,
      analytics,
      log,
      onRefreshData: refreshDataForSetup
    };

    await config.performSetup(setupOpts);

    if (currentScreen === SCREENS.PAYMENT_METHODS_LIST) {
      goBack();
    }
  }, [selectedPaymentMethod, paymentOptions, analytics, log, refreshDataForSetup, currentScreen, goBack]);

  // Processor callbacks
  const { processorCallbacks, screenCallbacks } = useProcessorCallbacks({
    chargeProcessing,
    analytics,
    showSuccessScreen,
    showErrorScreen,
    goBack,
    onPaymentComplete: handlers.handlePaymentComplete,
    onPaymentFailed: handlers.handlePaymentFailed,
    onSpendingLimits: onSpendingLimitAction,
    onPurchaseSuccess,
    resetSignal: useCallback(() => setStartSignal(0), [])
  });

  const processingComplete = chargeProcessing.state.status === 'error' || chargeProcessing.state.status === 'success';

  // ==========================================================================
  // Effects
  // ==========================================================================

  useEffect(() => {
    if (dataLoaded) return;

    const loadData = async () => {
      try {
        await fetchData(fetchParams.price, fetchParams.bonus, isPayg, fetchParams.creditsRequested);
      } catch {
        // Error state is handled by the `error` return value; just ensure we exit the loading state
      } finally {
        setDataLoaded(true);
        onReady?.();
      }
    };

    loadData();
  }, [fetchParams, dataLoaded, fetchData, onReady]);

  useNativeBackButton({
    mobileAppProps,
    onBackPress: handlers.handleBackClick,
    enabled: currentScreen !== SCREENS.PAYMENT_PROCESSING
  });

  useEffect(() => {
    if (!onScreenChange) return;
    const screenTitleKey = getScreenTitleKey(currentScreen, isPayg, mode, isMinPurchase);
    const title = screenTitleKey ? renderText(screenTitleKey) : '';
    const isInnerScreenNav = currentScreen === SCREENS.PAYMENT_METHODS_LIST
      || currentScreen === SCREENS.ADD_CREDIT_CARD;
    const isAdditionalFundsEntryNav = currentScreen === SCREENS.PURCHASE_DETAILS
      && mode === 'additionalFunds';
    const showBackButton = isInnerScreenNav || isAdditionalFundsEntryNav;
    const isProcessing = currentScreen === SCREENS.PAYMENT_PROCESSING;
    onScreenChange({
      screen: currentScreen,
      title,
      showBackButton,
      isProcessing,
      selectedPaymentKind: selectedPaymentMethod?.kind
    });
  }, [currentScreen, onScreenChange, isPayg, mode, isMinPurchase, renderText, selectedPaymentMethod?.kind]);

  useEffect(() => {
    setProcessingHasError(chargeProcessing.state.status === 'error');
  }, [chargeProcessing.state.status]);

  // Add funds screen: fire once when purchase details screen is shown with required credit (align with old trackMPEventAdditionalFundsRequired)
  // Only for additionalFunds mode — regular purchase, advisor bonus, quick topup, and affiliate promo don't track this event.
  useEffect(() => {
    const credit = purchaseDetails?.fees.creditsRequested;
    if (
      displayMode === 'button'
      || currentScreen !== SCREENS.PURCHASE_DETAILS
      || !dataLoaded
      || !credit
      || credit <= 0
      || addFundsScreenTrackedRef.current
    ) return;

    const reportAddFundsScreen = window.config?.reportAddFundsScreen;
    if (reportAddFundsScreen === 'no') return;
    if (reportAddFundsScreen === 'new' && !selectedPaymentMethod) return;

    const hasOptions = paymentOptions.length > 0 || paymentMethods.length > 0;
    if (!hasOptions) return;

    const params: Record<string, string | number | undefined> = {
      credit,
      'base price': feeAdjustedAmount,
      'display currency': currency?.key
    };

    if (!isPayg && metadata?.addFundsInitialProps) {
      Object.entries(metadata.addFundsInitialProps).forEach(([key, val]) => {
        if (val != null) params[key] = val;
      });
    }

    analytics.trackAddFundsScreen(params);
    addFundsScreenTrackedRef.current = true;
  }, [
    mode,
    currentScreen,
    dataLoaded,
    displayMode,
    purchaseDetails?.fees.creditsRequested,
    feeAdjustedAmount,
    currency?.key,
    isPayg,
    metadata?.addFundsInitialProps,
    selectedPaymentMethod,
    paymentOptions,
    paymentMethods,
    analytics
  ]);

  // Payment methods list screen (purchase flow): fire "payment sources screen" once when opened with data (align with old trackPaymentSourcesEventHandler in payment_methods_list.js)
  useEffect(() => {
    if (
      currentScreen !== SCREENS.PAYMENT_METHODS_LIST
      || !dataLoaded
      || loading
      || paymentMethodsListScreenTrackedRef.current
    ) return;
    if (paymentMethods.length <= 0) return;

    const defaultMethod = paymentMethods.find((m) => m.isDefault);
    const defaultKind = defaultMethod?.kind ?? 'none';
    const numberOfCreditCards = paymentMethods.filter((m) => m.kind === 'cc').length;
    analytics.trackPaymentSourcesScreen(defaultKind, numberOfCreditCards);
    paymentMethodsListScreenTrackedRef.current = true;
  }, [
    currentScreen,
    dataLoaded,
    loading,
    paymentMethods,
    analytics
  ]);

  // Navigate back to the processing screen if the processor reports activity
  // while we're on a different screen (e.g. user authorized on the GP sheet
  // after the 8s dismissal timeout navigated away). Skip for delegated payments.
  const chargeStatus = chargeProcessing.state.status;
  useEffect(() => {
    if (!isWebProcessedPayment(selectedPaymentMethod?.kind)) return;
    if (['processing', 'polling', 'authenticating'].includes(chargeStatus) && currentScreen !== SCREENS.PAYMENT_PROCESSING) {
      navigation.navigateTo(SCREENS.PAYMENT_PROCESSING);
    }
  }, [chargeStatus, currentScreen, navigation, selectedPaymentMethod?.kind]);

  useEffect(() => {
    setActionInProgress(false);
    if (currentScreen !== SCREENS.PAYMENT_PROCESSING) {
      setButtonProcessing(false);
    }
  }, [currentScreen]);

  // ==========================================================================
  // Derived Values
  // ==========================================================================

  const processingErrorType = chargeProcessing.state.error?.errorType;
  const headerConfig = getHeaderConfig(currentScreen, isPayg, processingComplete, processingErrorType, mode, isMinPurchase, allowCloseButton);

  const isMobileValidationError = !!(mobileAppProps && validationError);
  if (isMobileValidationError) {
    headerConfig.titleKey = '';
  }

  const statementNote = useMemo(
    () => {
      const isCreditCard = selectedPaymentMethod?.kind === 'cc';
      return getStatementNote(isCreditCard ?? false);
    },
    [selectedPaymentMethod?.kind]
  );

  const renderErrorOverlay = useCallback(
    () => <div className={ classes.errorOverlayIcon }>{renderIcon(PURCHASE_FLOW_ICONS.PROCESSING_ERROR)}</div>,
    [renderIcon]
  );
  const renderLogoOverlay = useCallback(
    () => <>{renderIcon(PURCHASE_FLOW_ICONS.APP_LOGO, classes.affiliatePromoLogo)}</>,
    [renderIcon]
  );

  const showErrorOverlay = currentScreen === SCREENS.PAYMENT_PROCESSING && processingHasError;
  const showLogoOverlay = mode === 'affiliatePromo'
    && currentScreen === SCREENS.PAYMENT_PROCESSING
    && processingComplete
    && !processingHasError;

  const renderTopOverlay = useMemo((): (() => React.ReactElement) | undefined => {
    if (showErrorOverlay) return renderErrorOverlay;
    if (showLogoOverlay) return renderLogoOverlay;
    return undefined;
  }, [showErrorOverlay, renderErrorOverlay, showLogoOverlay, renderLogoOverlay]);

  // ==========================================================================
  // Screen-specific Handlers
  // ==========================================================================

  const handleAddPaymentMethodClick = useCallback((kind: string) => {
    if (actionInProgress) return;
    setActionInProgress(true);

    const kindConfig = getKindConfig(kind);
    if (kindConfig?.opensPopup && !selectedPaymentMethod && isWebProcessedPayment(kind)) {
      clickContextRef.current = createPaymentPopup(log);
    }

    handlers.handleAddPaymentMethodClick(kind, setAddingPaymentMethodKind, () => setStartSignal((prev) => prev + 1))
      .finally(() => setActionInProgress(false));
  }, [handlers, actionInProgress, selectedPaymentMethod, log]);

  const handlePayClick = useCallback(() => {
    if (actionInProgress) return;

    setActionInProgress(true);
    clickContextRef.current = null;

    const kindConfig = selectedPaymentMethod ? getKindConfig(selectedPaymentMethod.kind) : null;
    const isQuickTopup = metadata?.topupFlow === 'quick';
    const needsGestureSession = !isQuickTopup && kindConfig?.nativeButton?.onClick;
    const needsPopup = !isQuickTopup && kindConfig?.opensPopup && isWebProcessedPayment(selectedPaymentMethod!.kind);
    // Google Pay (Checkout.com Flow): payment is already prepared and the Flow
    // GPay button is mounted. Open the sheet by firing the processor's
    // synchronous trigger right here — inside the live gesture (MBW-2436). The
    // processor ignores startSignal, so the increment below is a harmless no-op
    // for it and the path stays identical to the other methods.
    const rendersOwnPayButton = !isQuickTopup && kindConfig?.rendersOwnPayButton;

    if (needsGestureSession) {
      clickContextRef.current = kindConfig!.nativeButton!.onClick!(
        feeAdjustedAmount,
        selectedPaymentMethod!.context
      );
    } else if (needsPopup) {
      clickContextRef.current = createPaymentPopup(log);
    } else if (rendersOwnPayButton) {
      nativeTriggerRef.current?.();
    }

    if (!isPayg) {
      setStartSignal((prev) => prev + 1);
    }
    handlers.handlePayClick();
  }, [handlers, actionInProgress, selectedPaymentMethod, metadata?.topupFlow, feeAdjustedAmount, isPayg, log]);

  const handlePaymentMethodAdded = useCallback(async () => {
    setEditingPaymentSourceId(undefined);
    await handlers.handlePaymentMethodAdded(setAddingPaymentMethodKind);
    onPaymentMethodAdded?.(paymentMethods.length + 1);
  }, [handlers, onPaymentMethodAdded, paymentMethods.length]);

  const handleEditPaymentMethod = useCallback(async (paymentMethodId: number, kind: string) => {
    const method = paymentMethods.find((m) => m.id === paymentMethodId);
    const isSecurityReasons = method?.error === PAYMENT_SOURCE_ERRORS.PLEASE_RE_ENTER_CARD_DETAILS;

    if (isSecurityReasons) {
      try {
        await getPaymentSourceConfig(paymentMethodId);
        const response = await addPaymentSource(kind as PaymentMethodKind);
        setEditingPaymentSourceId(response.source.id);
      } catch {
        return;
      }
    } else {
      setEditingPaymentSourceId(paymentMethodId);
    }

    setAddingPaymentMethodKind(kind as PaymentMethodKind);
    navigation.navigateTo(SCREENS.ADD_CREDIT_CARD);
  }, [navigation, paymentMethods]);

  const handleCloseClick = useCallback(() => {
    if (processingHasError) {
      chargeProcessing.reset();
    }
    handlers.handleCloseClick(processingHasError, setProcessingHasError);
  }, [handlers, processingHasError, chargeProcessing]);

  const handleBackClick = useCallback(() => {
    if (currentScreen === SCREENS.ADD_CREDIT_CARD) {
      analytics.trackAddCardFailed('user cancel');
      setAddingPaymentMethodKind(null);
      setEditingPaymentSourceId(undefined);
    }
    handlers.handleBackClick();
  }, [currentScreen, handlers, analytics]);

  const focusProcessingWindow = useCallback(() => {
    const popup = clickContextRef.current?.window;
    if (popup && !popup.closed) popup.focus();
  }, []);

  const handleRetryClick = useCallback(() => {
    const { error: errorState } = chargeProcessing.state;

    screenCallbacks.handleRetryClick();
    setRecoverFlag(errorState?.recoverable || false);
    setRecoverBaseUrl(
      errorState?.recoverable
        ? `${ errorState.errorOrigin }${ errorState.errorPathname }`
        : undefined
    );

    const kindConfig = selectedPaymentMethod ? getKindConfig(selectedPaymentMethod.kind) : null;
    if (kindConfig?.opensPopup) {
      clickContextRef.current = createPaymentPopup(log);
    }

    setRetryKey((prev) => prev + 1);
  }, [screenCallbacks, chargeProcessing.state.error, selectedPaymentMethod, log]);

  // ==========================================================================
  // Return
  // ==========================================================================

  return {
    loading,
    error,
    dataLoaded,
    selectedPaymentMethod,
    paymentMethods,
    paymentOptions,
    purchaseDetails,
    price,
    bonus,
    feeAdjustedAmount,
    purchaseFeesText: purchaseDetails?.fees.purchaseFeesText,
    creditsRequested,
    isMinPurchase,
    detailsSlot,
    currency,
    fxRate: confirmedFxRate,

    supportCheckLoading,
    isSelectedMethodSupported,

    chargeProcessing,
    processingComplete,
    processingHasError,
    buttonProcessing,
    setButtonProcessing,

    actionInProgress,
    validating,
    validationError,
    dismissValidationError,
    handleValidationErrorBack,

    currentScreen,
    headerConfig,

    statementNote,
    renderTopOverlay,

    handlePayClick,
    handleBackClick,
    handleCloseClick,
    handleRetryClick,
    handleAddPaymentMethodClick,
    handlePaymentMethodAdded,
    handleEditPaymentMethod,
    handleLoadMethods,
    onPerformSetup,

    handlers,
    screenCallbacks,
    processorCallbacks,

    startSignal,
    retryKey,
    recoverFlag,
    recoverBaseUrl,
    clickContextRef,

    nativeTriggerRef,
    nativeButtonReady,
    handleNativeButtonReady,

    addingPaymentMethodKind,
    editingPaymentSourceId,

    focusProcessingWindow
  };
};
